package com.bikompanion.ui.home

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutLinearInEasing
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.windowsizeclass.ExperimentalMaterial3WindowSizeClassApi
import androidx.compose.material3.windowsizeclass.WindowSizeClass
import androidx.compose.material3.windowsizeclass.WindowWidthSizeClass
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import dev.chrisbanes.haze.hazeSource
import dev.chrisbanes.haze.rememberHazeState
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.location.LocationState
import org.maplibre.compose.location.rememberLocationState
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.rememberMapState
import org.maplibre.spatialk.geojson.Position
import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.LanguagePreference
import com.bikompanion.domain.MapLayerMode
import com.bikompanion.domain.StationWatch
import com.bikompanion.domain.ThemeMode
import com.bikompanion.ui.composables.GlassToast
import com.bikompanion.ui.composables.HomeFabColumn
import com.bikompanion.ui.composables.MapLayerControls
import com.bikompanion.ui.composables.STATIONS_VISIBLE_ZOOM
import com.bikompanion.ui.composables.STATION_NAMES_VISIBLE_ZOOM
import com.bikompanion.ui.composables.dockedPanelSpring
import com.bikompanion.ui.composables.nearestTo
import com.bikompanion.ui.composables.safeDrawingInsets
import com.bikompanion.ui.install.AppInstallOffer
import com.bikompanion.ui.install.requiredForWatchAlerts
import com.bikompanion.ui.link.StationLinks
import com.bikompanion.ui.models.BikeRatingUiState
import com.bikompanion.ui.models.DataFreshness
import com.bikompanion.ui.models.FoldBounds
import com.bikompanion.ui.models.LocateMode
import com.bikompanion.ui.models.ParkingAreaSummary
import com.bikompanion.ui.models.ParkingCountdownStatus
import com.bikompanion.ui.models.RefreshStatus
import com.bikompanion.ui.models.text
import com.bikompanion.ui.playground.GlassPlaygroundOverlay
import com.bikompanion.ui.preview.PreviewEmptyStation
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkExpanded
import com.bikompanion.ui.preview.PreviewLightDarkLandscape
import com.bikompanion.ui.preview.PreviewLightDarkMedium
import com.bikompanion.ui.preview.PreviewNetwork
import com.bikompanion.ui.preview.PreviewStation
import com.bikompanion.ui.settings.SettingsPage
import com.bikompanion.ui.settings.SettingsPlatform
import com.bikompanion.ui.settings.SettingsWindow
import com.bikompanion.ui.strings.LocalStrings
import com.bikompanion.ui.theme.AppTheme
import kotlin.time.ComparableTimeMark
import kotlin.time.Duration.Companion.milliseconds

// Out fast and linear, back in with the same bounce the navigation bar's indicator travels on, so
// the points look like they are answering the tab rather than running an animation of their own.
// The shrink is short on purpose: it is time before the layer the user asked for is on screen.
private val MarkerShrinkSpec = tween<Float>(durationMillis = 140, easing = FastOutLinearInEasing)

private val MarkerGrowSpec = spring<Float>(
    dampingRatio = Spring.DampingRatioLowBouncy,
    stiffness = Spring.StiffnessMediumLow,
)

private val StationEaseDuration = 700.milliseconds

private val StationFlyDuration = 1200.milliseconds

/**
 * How the camera gets to a station it was sent to.
 *
 * A station already on screen is reached with a direct ease: [CameraAnimation.Fly] zooms out
 * before it zooms in, and the zoom-out dips below [STATIONS_VISIBLE_ZOOM], where the map swaps
 * station markers for network circles — so the stations vanished mid-flight and only came back once
 * the camera had landed. A far station still needs the zoom-out to stay legible over the distance,
 * but the default flight is paced by the distance and the 5-6 zoom levels it closes, which read as
 * crawling, so its duration is capped.
 */
private fun stationFocusAnimation(
    station: BikeStation,
    cameraState: MapState,
): CameraAnimation {
    val bounds = cameraState.viewport?.visibleBounds
    val inView = bounds != null &&
        station.latitude in bounds.south..bounds.north &&
        station.longitude in bounds.west..bounds.east
    return if (inView) {
        CameraAnimation.Ease(from = CameraAnimation.Ease.Standard) { duration = StationEaseDuration }
    } else {
        CameraAnimation.Fly(from = CameraAnimation.Fly.Standard) { duration = StationFlyDuration }
    }
}

// Same layer as the nav bar: they never overlap, and anything >= 1 stays out of the edge blur.
private const val FabHazeZIndex = 1f

// Over every sheet and the settings window, which is where a "Link copied" has to be read.
private const val SnackbarZIndex = 5f

private const val AppShareTitle = "Bikompanion"

private val AppInstallToastGap = 8.dp

// The bar's capsule reaches 78dp up from the bottom: its footprint without the margin above it.
private val AppInstallToastBarOffset = NavBarFootprint - 16.dp + AppInstallToastGap

private val DockedPanelWidth = 380.dp

private val DockedPanelMargin = 16.dp

// How far a docked sheet reaches in from the end edge (380dp panel + 16dp margin), which the
// end-corner controls step aside by so the panel does not cover them.
private val DockedPanelClearance = DockedPanelWidth + DockedPanelMargin

@OptIn(ExperimentalMaterial3WindowSizeClassApi::class)
@Composable
internal fun HomeContent(
    darkTheme: Boolean,
    themeMode: ThemeMode,
    onThemeModeSelect: (ThemeMode) -> Unit,
    language: LanguagePreference,
    onLanguageSelect: (LanguagePreference) -> Unit,
    settingsPage: SettingsPage?,
    onSettingsPageOpen: (SettingsPage) -> Unit,
    onSettingsBack: () -> Unit,
    onSettingsClose: () -> Unit,
    networks: List<BikeNetwork>,
    stations: List<BikeStation>,
    parkingSpots: List<BikeParkingSpot>,
    parkingAreas: List<ParkingAreaSummary>,
    isLoadingStations: Boolean,
    noNetworkInView: Boolean,
    isLoadingParking: Boolean,
    parkingLoadFailed: Boolean,
    onRetryLoadParkingClick: () -> Unit,
    changedAt: Map<String, ComparableTimeMark>,
    cameraState: MapState,
    locationState: LocationState,
    networksLoadFailed: Boolean,
    refreshStatus: RefreshStatus,
    freshness: DataFreshness,
    parkingCountdownStatus: ParkingCountdownStatus,
    mapLayerMode: MapLayerMode,
    onMapLayerModeSelect: (MapLayerMode) -> Unit,
    ratingUiState: BikeRatingUiState,
    onRatingBikeNumberChange: (String) -> Unit,
    onRatingElectricToggle: (Boolean) -> Unit,
    onVoteSelect: (Boolean) -> Unit,
    onIssueToggle: (BikeIssue) -> Unit,
    onRatingNetworkSelect: (BikeNetwork) -> Unit,
    onRatingPositionChange: (Double, Double, BikeScopeSource) -> Unit,
    onSubmitVoteClick: () -> Unit,
    onRatingDialogDismiss: () -> Unit,
    watches: List<StationWatch>,
    onWatch: (BikeStation, BikeType) -> Unit,
    onCancelWatch: (BikeStation, BikeType) -> Unit,
    stationFocusTarget: StationFocusTarget?,
    onStationFocusHandle: (StationFocusTarget) -> Unit,
    selectedStationTarget: StationFocusTarget?,
    onStationSelect: (StationFocusTarget) -> Unit,
    onStationDismiss: () -> Unit,
    locateMode: LocateMode,
    onCompassClick: () -> Unit,
    onLocateMeClick: () -> Unit,
    modifier: Modifier = Modifier,
    foldBounds: FoldBounds? = null,
    shareBaseUrl: String = StationLinks.ProductionBaseUrl,
    onShareLink: ((title: String, url: String) -> Boolean)? = null,
    settingsPlatform: SettingsPlatform = SettingsPlatform(),
    appInstallOffer: AppInstallOffer? = null,
    appInstallDismissed: Boolean = false,
    onAppInstallDismiss: () -> Unit = {},
    showGlassPlayground: Boolean = false,
    showDebugNotificationButton: Boolean = false,
    onDebugNotificationClick: () -> Unit = {},
) {
    var showRatingSheet by remember { mutableStateOf(false) }
    val currentOnRatingDialogDismiss by rememberUpdatedState(onRatingDialogDismiss)
    val currentOnStationFocusHandle by rememberUpdatedState(onStationFocusHandle)
    RatingPositionEffect(
        sheetOpen = showRatingSheet,
        locationState = locationState,
        cameraState = cameraState,
        onRatingPositionChange = onRatingPositionChange,
    )
    val snackbarHostState = remember { SnackbarHostState() }
    val snackbarScope = rememberCoroutineScope()
    val cameraScope = rememberCoroutineScope()
    // A submit that ends the sheet's job — it landed, or it was refused because this bike was
    // already rated — is a one-shot event, not state to keep drawing: the sheet closes and the
    // toast carries the outcome. The sheet is dismissed the same way a manual close is, so a
    // stale bike number/score from this vote can't linger into the next one.
    //
    // The toast is launched into the composition's own scope rather than shown from here: the
    // dismissal clears `toastMessage`, which re-keys this effect and would cancel a snackbar
    // still being awaited, so the message would never appear.
    val strings = LocalStrings.current
    LaunchedEffect(ratingUiState.toastMessage) {
        val message = ratingUiState.toastMessage ?: return@LaunchedEffect
        showRatingSheet = false
        currentOnRatingDialogDismiss()
        snackbarScope.launch { snackbarHostState.showSnackbar(message.text(strings)) }
    }
    // The identity rather than the station: holding the value would freeze the sheet's counts at
    // whatever they were when it was tapped, while the marker behind it kept updating. It comes
    // from the back stack, not from state here, so the address bar and the sheet cannot disagree.
    val currentOnStationSelect by rememberUpdatedState(onStationSelect)
    val currentOnStationDismiss by rememberUpdatedState(onStationDismiss)
    val shareStation = rememberStationShare(
        baseUrl = shareBaseUrl,
        onShareLink = onShareLink,
        snackbarHostState = snackbarHostState,
    )
    val shareLink = rememberLinkShare(onShareLink = onShareLink, snackbarHostState = snackbarHostState)
    val selectedStation = selectedStationTarget?.let { target ->
        stations.firstOrNull { it.id == target.stationId && it.networkId == target.networkId }
    }
    var selectedParkingId by remember { mutableStateOf<String?>(null) }
    val selectedParking = selectedParkingId?.let { id -> parkingSpots.firstOrNull { it.id == id } }
    // The layer the markers on screen belong to, which lags [mapLayerMode] by the time it takes
    // the outgoing points to shrink away: swapping the lists the instant the tab is tapped makes
    // one set of points blink out and another blink in, with nothing saying they replaced it.
    var drawnMode by remember { mutableStateOf(mapLayerMode) }
    val markerScale = remember { Animatable(1f) }
    LaunchedEffect(mapLayerMode) {
        // Not `else return` — a tap back before the shrink finishes leaves this effect restarted
        // with the mode it already had and a scale still on its way down, which then has to grow.
        if (drawnMode != mapLayerMode) {
            markerScale.animateTo(0f, MarkerShrinkSpec)
            drawnMode = mapLayerMode
        }
        markerScale.animateTo(1f, MarkerGrowSpec)
    }
    val showingStations = drawnMode == MapLayerMode.Stations
    // A sheet left open across the switch would be describing a marker no longer on the map.
    var previousShowingStations by remember { mutableStateOf(showingStations) }
    LaunchedEffect(showingStations) {
        if (showingStations != previousShowingStations) {
            selectedParkingId = null
            if (!showingStations) currentOnStationDismiss()
            previousShowingStations = showingStations
        }
    }
    // MaplibreMap remembers its callbacks object, so the click lambda it holds is the one from
    // the first composition — when no stations had loaded yet. Reading them through a State
    // keeps that long-lived lambda looking at the current list instead of an empty one.
    val currentStations by rememberUpdatedState(stations)
    val currentParkingSpots by rememberUpdatedState(parkingSpots)
    val currentShowingStations by rememberUpdatedState(showingStations)
    val hazeState = rememberHazeState()

    // At Medium width the bottom bar becomes a side rail; at Expanded the sheets also dock to the
    // end edge instead of rising from the bottom, which keeps the map visible next to what was
    // tapped.
    //
    // Derived from the layout's own size rather than `calculateWindowSizeClass()`, whose Android
    // actual needs an `Activity` that shared code does not have.
    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        val widthSizeClass = WindowSizeClass.calculateFromSize(DpSize(maxWidth, maxHeight)).widthSizeClass
        val useRail = widthSizeClass != WindowWidthSizeClass.Compact
        // A fold splits the window in two, so the panel takes the half past it whatever the
        // width class says: an unfolded foldable can report Medium, which used to leave it with
        // bottom sheets that ran straight across the hinge.
        val dockSheets = widthSizeClass == WindowWidthSizeClass.Expanded || foldBounds != null
        // The panel and the controls both sit inside the end inset, so a fold measured from the
        // window edge has to give it back or the two land unevenly either side of the crease: the
        // panel's edge drifts toward it by the inset while the controls drift away. Physical right,
        // because the fold bounds are in window coordinates.
        val endInset = safeDrawingInsets().asPaddingValues().calculateRightPadding(LayoutDirection.Ltr)
        val dockedPanelWidth = foldBounds?.let { maxWidth - it.end - DockedPanelMargin * 2 - endInset }
            ?: DockedPanelWidth
        val dockedPanelClearance = foldBounds?.let { maxWidth - it.start - endInset } ?: DockedPanelClearance
        val dockedPanelOpen = dockSheets &&
            (selectedStation != null || selectedParking != null || showRatingSheet)
        // The end-corner controls slide out of the way of an open docked panel rather than jump.
        // On the panel's own spring so the two move as one: a separate tween had the controls and
        // panel start and stop at different moments, which looked harsh.
        val endClearance by animateDpAsState(
            targetValue = if (dockedPanelOpen) dockedPanelClearance else 0.dp,
            animationSpec = dockedPanelSpring(),
        )
        // The controls that share the bottom edge rest on the install toast rather than sit under it.
        // Measured because its height depends on how the copy wraps in each language.
        var appInstallToastHeight by remember { mutableStateOf(0.dp) }
        val appInstallClearance by animateDpAsState(
            targetValue = if (appInstallOffer != null && !appInstallDismissed) {
                appInstallToastHeight + AppInstallToastGap
            } else {
                0.dp
            },
        )
        // Where a focused station is framed: in what the docked panel and the navigation bar leave
        // of the screen, which is half of each one's size away from its centre. The rail takes the
        // start edge and the bottom bar the bottom one.
        //
        // Read through State so a resize mid-flight does not restart the effect below, which
        // would fly the camera to the station a second time.
        val safeInsets = safeDrawingInsets().asPaddingValues()
        val railClearance = if (useRail) NavRailFootprint + safeInsets.calculateLeftPadding(LayoutDirection.Ltr) else 0.dp
        val barClearance = if (useRail) 0.dp else NavBarFootprint + safeInsets.calculateBottomPadding()
        val currentFocusOffset by rememberUpdatedState(
            ((if (dockSheets) dockedPanelClearance else 0.dp) - railClearance) / 2 to barClearance / 2,
        )
        LaunchedEffect(stationFocusTarget, stations) {
            val target = stationFocusTarget ?: return@LaunchedEffect
            val resolution = resolveStationFocus(target, stations, cameraState.cameraPosition.zoom)
                ?: return@LaunchedEffect
            if (showRatingSheet) {
                showRatingSheet = false
                currentOnRatingDialogDismiss()
            }
            selectedParkingId = null
            currentOnStationSelect(resolution.target)
            // Handled before the camera moves, and the flight launched in the composition's scope
            // rather than awaited here: handling clears the target, which re-keys this effect and
            // would cancel a flight still running inside it.
            currentOnStationFocusHandle(resolution.target)
            val camera = cameraState.cameraPosition
            // Centred in the space the docked panel leaves free rather than on the screen, which
            // would put the station underneath the panel.
            val center = Position(longitude = resolution.station.longitude, latitude = resolution.station.latitude)
                .centreForScreenOffset(
                    left = currentFocusOffset.first,
                    up = currentFocusOffset.second,
                    zoom = resolution.zoom,
                    bearing = camera.bearing,
                )
            val animation = stationFocusAnimation(resolution.station, cameraState)
            cameraScope.launch {
                cameraState.animateCamera(CameraUpdate(center = center, zoom = resolution.zoom), animation)
            }
        }
        // hazeSource lives on this inner Box alone, not the outer one: the sheet below is a
        // sibling of it, drawn on top. If the sheet were inside this Box too, hazeSource would
        // capture the sheet along with the map, and the sheet would end up blurring itself.
        // Left at the default zIndex 0 — the bottom layer of the stack the nav bar sits above.
        HomeMapArea(
            cameraState = cameraState,
            darkTheme = darkTheme,
            hazeState = hazeState,
            onStationTap = { offset ->
                // Read through the State for the same reason the list is: this lambda is the
                // one captured on first composition, when the map was still on stations.
                if (currentShowingStations) {
                    val hit = currentStations.nearestTo(offset, cameraState)
                    if (hit != null) {
                        currentOnStationSelect(StationFocusTarget(hit.networkId, hit.id))
                        return@HomeMapArea ClickResult.Consume
                    }
                } else {
                    val hit = currentParkingSpots.nearestTo(offset, cameraState)
                    if (hit != null) {
                        selectedParkingId = hit.id
                        return@HomeMapArea ClickResult.Consume
                    }
                }
                ClickResult.Pass
            },
            onCompassClick = onCompassClick,
            // Networks stay either way: zoomed out past STATIONS_VISIBLE_ZOOM the overlay
            // draws one circle per network, which is the right reading of "where is there
            // anything" whichever layer is selected.
            // Emptied rather than hidden — the overlay draws whatever it is handed, and only
            // one layer's markers are ever non-empty. Networks go with the stations: a
            // network circle answers "is there a bike-share system here", which is the wrong
            // question entirely while the rider is looking for somewhere to lock up.
            networks = if (showingStations) networks else emptyList(),
            stations = if (showingStations) stations else emptyList(),
            parkingSpots = if (showingStations) emptyList() else parkingSpots,
            parkingAreas = if (showingStations) emptyList() else parkingAreas,
            changedAt = changedAt,
            // A lambda, so the frames of the swap only invalidate the overlay's draw phase.
            markerScale = { markerScale.value },
        )

        ScreenEdgeBlur(
            hazeState = hazeState,
            modifier = Modifier.fillMaxSize(),
        )

        MapLayerControls(
            mapState = cameraState,
            darkTheme = darkTheme,
            onCompassClick = onCompassClick,
            modifier = Modifier
                .align(Alignment.TopEnd)
                .windowInsetsPadding(safeDrawingInsets())
                .padding(top = 8.dp, end = 8.dp),
        )

        // Controls are siblings of the edge blur rather than content of the captured map: their
        // pixels stay crisp while the map directly underneath the system edge is softened.
        // Stacked rather than placed in opposite corners: both are status about when something
        // next happens, and reading them as one column beats hunting for the second one across
        // the map.
        HomeStatusColumn(
            refreshStatus = refreshStatus,
            freshness = freshness,
            parkingCountdownStatus = parkingCountdownStatus,
            isLoadingStations = isLoadingStations,
            showNoNetworkHere = showingStations && noNetworkInView,
            isLoadingParking = isLoadingParking,
            showParkingLoadFailed = !showingStations && parkingLoadFailed,
            onRetryLoadParkingClick = onRetryLoadParkingClick,
            hazeState = hazeState,
            modifier = Modifier
                .align(Alignment.TopStart)
                .windowInsetsPadding(safeDrawingInsets())
                .padding(16.dp),
        )

        HomeFabColumn(
            onSettingsClick = {
                // A docked panel would stay on screen beside the window, dead under its click-catcher.
                showRatingSheet = false
                onRatingDialogDismiss()
                selectedParkingId = null
                onStationDismiss()
                onSettingsPageOpen(SettingsPage.Main)
            },
            locateMode = locateMode,
            onLocateMeClick = onLocateMeClick,
            showDebugNotificationButton = showDebugNotificationButton,
            onDebugNotificationClick = onDebugNotificationClick,
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .windowInsetsPadding(safeDrawingInsets())
                // Positioned above the bottom nav bar; the rail is at the side, so no lift is needed.
                // Beside the rail the toast is narrow enough to leave this corner clear.
                .padding(end = 16.dp + endClearance, bottom = if (useRail) 16.dp else 104.dp + appInstallClearance)
                // The sheets' glass only draws registered sources, so without this the FABs
                // vanish under an open sheet instead of refracting through it. Kept at zIndex >= 1
                // so the edge blur, which only samples map-level sources, leaves them crisp.
                .hazeSource(hazeState, zIndex = FabHazeZIndex),
        )

        SnackbarHost(
            hostState = snackbarHostState,
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .zIndex(SnackbarZIndex)
                .windowInsetsPadding(safeDrawingInsets())
                .padding(
                    start = 16.dp,
                    end = 16.dp,
                    top = 16.dp,
                    bottom = (if (useRail) 16.dp else 104.dp) + appInstallClearance,
                ),
        )

        GlassToast(
            show = networksLoadFailed,
            message = LocalStrings.current.networksLoadFailed,
            hazeState = hazeState,
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .zIndex(SnackbarZIndex)
                .windowInsetsPadding(safeDrawingInsets())
                // Clears the nav bar and the check-bikes button below it.
                .padding(
                    start = 16.dp,
                    end = 16.dp + endClearance,
                    bottom = (if (useRail) 88.dp else 148.dp) + appInstallClearance,
                ),
        )

        // Outside the hazeSource box for the same reason the sheets are: a hazeGlass node drawn
        // inside the content that hazeSource captures samples itself, and the blur renders as
        // nothing. It registers itself as a source one zIndex above the map, so the sheets below —
        // drawn after it, and consuming every source — refract the bar rather than hiding it.
        HomeNavigationBar(
            selectedMode = mapLayerMode,
            onModeSelect = onMapLayerModeSelect,
            hazeState = hazeState,
            modifier = Modifier.align(if (useRail) Alignment.CenterStart else Alignment.BottomCenter),
            vertical = useRail,
        )

        CheckBikesGlassButton(
            visible = mapLayerMode == MapLayerMode.Stations,
            hazeState = hazeState,
            onClick = { showRatingSheet = true },
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .windowInsetsPadding(safeDrawingInsets())
                // Centred in the space left beside a docked panel; the rail is ignored on purpose.
                .padding(end = endClearance)
                // Preserve the 8dp gap above the nav bar; the button reserves 12dp internally
                // for its Android elevation shadow during the enter/exit transition.
                .padding(bottom = (if (useRail) 8.dp else 74.dp) + appInstallClearance),
        )

        if (appInstallOffer != null) {
            val density = LocalDensity.current
            AppInstallToast(
                visible = !appInstallDismissed,
                offer = appInstallOffer,
                hazeState = hazeState,
                onDismiss = onAppInstallDismiss,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .windowInsetsPadding(safeDrawingInsets())
                    .padding(
                        start = 16.dp,
                        end = 16.dp + endClearance,
                        bottom = if (useRail) 16.dp else AppInstallToastBarOffset,
                    ).onSizeChanged { size ->
                        appInstallToastHeight = with(density) { size.height.toDp() }
                    },
            )
        }

        HomeSheets(
            selectedStation = selectedStation,
            selectedParking = selectedParking,
            showRatingSheet = showRatingSheet,
            networks = networks,
            ratingUiState = ratingUiState,
            watches = watches,
            hazeState = hazeState,
            docked = dockSheets,
            dockedWidth = dockedPanelWidth,
            watchRequiresInstall = appInstallOffer.requiredForWatchAlerts,
            onStationDismiss = onStationDismiss,
            onShareStation = shareStation,
            onParkingDismiss = { selectedParkingId = null },
            onWatch = onWatch,
            onCancelWatch = onCancelWatch,
            onRatingBikeNumberChange = onRatingBikeNumberChange,
            onRatingElectricToggle = onRatingElectricToggle,
            onVoteSelect = onVoteSelect,
            onIssueToggle = onIssueToggle,
            onRatingNetworkSelect = onRatingNetworkSelect,
            onSubmitVoteClick = onSubmitVoteClick,
            onRatingDismiss = {
                showRatingSheet = false
                onRatingDialogDismiss()
            },
        )

        SettingsWindow(
            page = settingsPage,
            themeMode = themeMode,
            platform = settingsPlatform,
            hazeState = hazeState,
            floating = useRail,
            onThemeModeSelect = onThemeModeSelect,
            language = language,
            onLanguageSelect = onLanguageSelect,
            onShareApp = { shareLink(AppShareTitle, StationLinks.ProductionBaseUrl) },
            onPageOpen = onSettingsPageOpen,
            onBack = onSettingsBack,
            onClose = onSettingsClose,
            modifier = Modifier.fillMaxSize(),
            // Not gated on the toast's dismissal: this is where a rider who closed it can come back.
            appInstallOffer = appInstallOffer,
        )

        // Dev-only: checks hazeGlass against the real map instead of a synthetic backdrop.
        // Remove once done tuning — see GlassPlaygroundScreen.kt.
        if (showGlassPlayground) {
            GlassPlaygroundOverlay(hazeState = hazeState, modifier = Modifier.fillMaxSize())
        }
    }
}

// One preview per window width class, since the rail and the docked sheets switch on the size the
// screen is laid out in: Compact (bottom bar), Medium (side rail) and Expanded (rail + side panel).
@PreviewLightDarkCompact
@Composable
private fun HomeScreenPreview() {
    HomeScreenPreviewContent()
}

@PreviewLightDarkMedium
@Composable
private fun HomeScreenMediumPreview() {
    HomeScreenPreviewContent()
}

@PreviewLightDarkExpanded
@Composable
private fun HomeScreenExpandedPreview() {
    HomeScreenPreviewContent()
}

@PreviewLightDarkLandscape
@Composable
private fun HomeScreenLandscapePreview() {
    HomeScreenPreviewContent()
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenParkingPreview() {
    HomeScreenPreviewContent(mapLayerMode = MapLayerMode.Parking, isLoadingParking = true)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenStationsLoadingPreview() {
    HomeScreenPreviewContent(isLoadingStations = true)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenNoNetworkHerePreview() {
    HomeScreenPreviewContent(noNetworkInView = true)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenParkingFailedPreview() {
    HomeScreenPreviewContent(mapLayerMode = MapLayerMode.Parking, parkingLoadFailed = true)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenNetworksFailedPreview() {
    HomeScreenPreviewContent(networksLoadFailed = true)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenStaleDataPreview() {
    HomeScreenPreviewContent(refreshStatus = RefreshStatus.Idle, freshness = DataFreshness(isStale = true))
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenPickupCountdownPreview() {
    HomeScreenPreviewContent(parkingCountdownStatus = ParkingCountdownStatus.Counting(secondsRemaining = 545))
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenStationSelectedPreview() {
    HomeScreenPreviewContent(selectedStation = StationFocusTarget(PreviewStation.networkId, PreviewStation.id))
}

@PreviewLightDarkExpanded
@Composable
private fun HomeScreenStationDockedPreview() {
    HomeScreenPreviewContent(selectedStation = StationFocusTarget(PreviewStation.networkId, PreviewStation.id))
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenSettingsPreview() {
    HomeScreenPreviewContent(settingsPage = SettingsPage.Main)
}

@PreviewLightDarkMedium
@Composable
private fun HomeScreenSettingsFloatingPreview() {
    HomeScreenPreviewContent(settingsPage = SettingsPage.Main)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenDebugPreview() {
    HomeScreenPreviewContent(showDebugNotificationButton = true)
}

@PreviewLightDarkCompact
@Composable
private fun HomeScreenAppInstallPreview() {
    HomeScreenPreviewContent(appInstallOffer = AppInstallOffer.AddToHomeScreen)
}

@PreviewLightDarkMedium
@Composable
private fun HomeScreenAppInstallRailPreview() {
    HomeScreenPreviewContent(appInstallOffer = AppInstallOffer.AddToHomeScreen)
}

@Composable
private fun HomeScreenPreviewContent(
    mapLayerMode: MapLayerMode = MapLayerMode.Stations,
    isLoadingStations: Boolean = false,
    noNetworkInView: Boolean = false,
    isLoadingParking: Boolean = false,
    parkingLoadFailed: Boolean = false,
    networksLoadFailed: Boolean = false,
    refreshStatus: RefreshStatus = RefreshStatus.Waiting(secondsRemaining = 24),
    freshness: DataFreshness = DataFreshness(),
    parkingCountdownStatus: ParkingCountdownStatus = ParkingCountdownStatus.Idle,
    selectedStation: StationFocusTarget? = null,
    settingsPage: SettingsPage? = null,
    showDebugNotificationButton: Boolean = false,
    appInstallOffer: AppInstallOffer? = null,
) {
    val cameraState = rememberMapState(
        initialCameraPosition = CameraPosition(
            center = Position(longitude = PreviewNetwork.longitude, latitude = PreviewNetwork.latitude),
            zoom = STATION_NAMES_VISIBLE_ZOOM,
        ),
    )
    var selectedStationTarget by remember { mutableStateOf(selectedStation) }
    AppTheme {
        HomeContent(
            darkTheme = isSystemInDarkTheme(),
            themeMode = ThemeMode.Auto,
            onThemeModeSelect = {},
            language = LanguagePreference.System,
            onLanguageSelect = {},
            settingsPage = settingsPage,
            onSettingsPageOpen = {},
            onSettingsBack = {},
            onSettingsClose = {},
            networks = listOf(PreviewNetwork),
            stations = listOf(PreviewStation, PreviewEmptyStation),
            parkingSpots = emptyList(),
            parkingAreas = emptyList(),
            isLoadingStations = isLoadingStations,
            noNetworkInView = noNetworkInView,
            isLoadingParking = isLoadingParking,
            parkingLoadFailed = parkingLoadFailed,
            onRetryLoadParkingClick = {},
            changedAt = emptyMap(),
            cameraState = cameraState,
            locationState = rememberLocationState(enabled = false),
            networksLoadFailed = networksLoadFailed,
            refreshStatus = refreshStatus,
            freshness = freshness,
            parkingCountdownStatus = parkingCountdownStatus,
            mapLayerMode = mapLayerMode,
            onMapLayerModeSelect = {},
            ratingUiState = BikeRatingUiState(),
            onRatingBikeNumberChange = {},
            onRatingElectricToggle = {},
            onVoteSelect = {},
            onIssueToggle = {},
            onRatingNetworkSelect = {},
            onRatingPositionChange = { _, _, _ -> },
            onSubmitVoteClick = {},
            onRatingDialogDismiss = {},
            watches = emptyList(),
            onWatch = { _, _ -> },
            onCancelWatch = { _, _ -> },
            stationFocusTarget = null,
            onStationFocusHandle = {},
            selectedStationTarget = selectedStationTarget,
            onStationSelect = { selectedStationTarget = it },
            onStationDismiss = { selectedStationTarget = null },
            locateMode = LocateMode.Off,
            onCompassClick = {},
            onLocateMeClick = {},
            appInstallOffer = appInstallOffer,
            showDebugNotificationButton = showDebugNotificationButton,
        )
    }
}
